import React from "react";
import { Phone, MessageCircle, Send, Sparkles, Check } from "lucide-react";
import { SITE_CONFIG, telHref, telegramLink } from "../lib/config";
import { trackCall, trackEvent } from "../lib/analytics";

export default function Hero({ lang, T, scrollTo }) {
  const trustList = T.hero.trust[lang];

  return (
    <section id="top" className="relative pt-28 md:pt-32 pb-12 md:pb-24 overflow-hidden">
      {/* Background layers */}
      <div className="airflow-bg" />
      <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full hero-blob" />
      <div className="absolute top-1/4 right-0 w-[420px] h-[420px] rounded-full hero-blob opacity-40" />
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="grain" />

      <div className="relative mx-auto max-w-7xl px-4 md:px-6 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 reveal">
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="chip" data-testid="hero-chip">
              <Sparkles size={12} /> {T.hero.chip[lang]}
            </span>
            <span className="chip" style={{ background: "rgba(255,90,60,.08)", borderColor: "rgba(255,90,60,.3)", color: "#FF8A6B" }}>
              {T.hero.urgency[lang]}
            </span>
          </div>

          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-tight">
            <span className="block">{T.hero.h1[lang]}</span>
            <span className="block mt-2 bg-gradient-to-r from-ice-400 via-ice-300 to-ice-400 bg-clip-text text-transparent">
              Air Cooler · {SITE_CONFIG.brand}
            </span>
          </h1>

          <p className="mt-5 text-muted max-w-2xl text-[16px] md:text-lg leading-relaxed">
            {T.hero.sub[lang]}
          </p>

          {/* Price + microcopy */}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-3 glass rounded-2xl px-4 py-2.5" data-testid="hero-price-badge">
              <span className="text-xs uppercase tracking-widest text-muted">Price</span>
              <span className="font-display font-bold text-2xl text-white">{T.hero.priceBadge[lang]}</span>
            </div>
            <span className="text-sm text-muted">{T.hero.priceMicro[lang]}</span>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={telHref}
              onClick={() => trackCall("hero")}
              data-testid="hero-call-button"
              className="cta-pulse inline-flex items-center justify-center gap-3 btn-call rounded-full px-7 py-4 text-lg shadow-flame"
            >
              <Phone size={22} strokeWidth={2.6} />
              <span>{T.cta.callNow[lang]}</span>
            </a>

            <button
              onClick={() => {
                trackEvent("QuizStart", { source: "hero" });
                scrollTo("quiz");
              }}
              data-testid="hero-quiz-button"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 bg-white/5 border border-white/10 text-white hover:bg-white/10 transition"
            >
              <Sparkles size={18} className="text-ice-300" />
              {T.cta.pickModel[lang]}
            </button>

            <a
              href={telegramLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("ClickTelegram", { source: "hero", language: lang })}
              data-testid="hero-telegram-button"
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-white font-semibold transition hover:brightness-110 shadow-lg"
              style={{ background: "#229ED9" }}
            >
              <Send size={18} />
              {T.cta.writeTelegram[lang]}
            </a>
          </div>

          <p className="mt-4 text-sm text-muted max-w-xl">{T.hero.microUnderCTA[lang]}</p>

          {/* Trust strip */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-2 max-w-2xl">
            {trustList.map((tt, i) => (
              <div
                key={i}
                className="flex items-center gap-2 text-xs md:text-sm text-muted bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2"
                data-testid={`hero-trust-${i}`}
              >
                <Check size={14} className="text-ice-400 shrink-0" />
                <span>{tt}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Hero visual */}
        <div className="lg:col-span-5 reveal">
          <div className="relative">
            <div className="absolute -inset-6 rounded-[36px] bg-gradient-to-br from-ice-400/30 via-ice-300/10 to-transparent blur-2xl" />
            <div className="relative rounded-[28px] overflow-hidden glass animate-floatSlow">
              <picture>
                <source media="(min-width: 768px)" srcSet="/images/aircooler/1.png" />
                <img
                  src="/images/aircooler/2.png"
                  alt={lang === "uz" ? "Salqin havo oqimi bilan mobil suvli Air Cooler" : "Мобильный водяной охладитель воздуха Air Cooler с холодным потоком воздуха"}
                  className="w-full h-auto object-cover"
                  fetchpriority="high"
                  loading="eager"
                  data-testid="hero-product-image"
                />
              </picture>
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="glass rounded-2xl px-3 py-2 text-xs text-white/90">
                  <span className="text-ice-300 font-bold">Z-1050 → Z-900</span>
                  <span className="text-muted ml-2">5 models</span>
                </div>
                <div className="glass rounded-2xl px-3 py-2 text-xs">
                  <span className="text-white font-bold">170$+</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
